import { asset } from "@/composables/useAsset";

/**
 * Contains parameters about a label displayed on the
 * "Labels" overlay.
 */
export interface LabelItemData {
	/**
	 * Label ID
	 */
	id: string;
	/**
	 * Label custom title
	 */
	title: string;
	/**
	 * Label is enabled ?
	 */
	enabled: boolean;
	/**
	 * Label placeholder tag to use if mode is "placeholder"
	 */
	placeholder: (typeof LabelItemPlaceholderList)[number]["tag"] | "";
	/**
	 * HTML content if mode is "html"
	 */
	html: string;
	/**
	 * Content set by a trigger when mode is "placeholder" and placeholder is
	 * "TRIGGER". Stored per-label (rather than on the shared global "TRIGGER"
	 * placeholder) so multiple labels bound to "TRIGGER" can be controlled
	 * independently. Supports HTML.
	 */
	triggerContent?: string;
	/**
	 * Custom CSS for this label if mode is "html"
	 */
	css: string;
	/**
	 * Mode of this label.
	 * "placeholder": use a predefined placeholder
	 * "html": use custom HTML content
	 */
	mode: "placeholder" | "html";
	/**
	 * Font size in pixels
	 */
	fontSize: number;
	/**
	 * Font family
	 */
	fontFamily: string;
	/**
	 * Font color
	 */
	fontColor: string;
	/**
	 * Text horizontal alignment
	 */
	textAlign: "left" | "center" | "right";
	/**
	 * Should scroll content ?
	 */
	scrollContent: boolean;
	/**
	 * Show background ?
	 */
	backgroundEnabled: boolean;
	/**
	 * Background color
	 */
	backgroundColor: string;
}

/**
 * Contains definition about a LabelItemData placeholder
 */
export interface LabelItemPlaceholder {
	tag: LabelItemPlaceholderTag;
	type: (typeof LabelItemPlaceholderList)[number]["type"];
	descriptionKey: string;
	descriptionKeyName?: string;
	example: string;
}

type PlaceholderType =
	| "string"
	| "number"
	| "image"
	| "date"
	| "time"
	| "datetime"
	| "day"
	| "month"
	| "year"
	| "hours"
	| "minutes"
	| "seconds"
	| "duration";
type PlaceholderCategory =
	| "_trigger_"
	| "date"
	| "twitch"
	| "youtube"
	| "generic"
	| "kofi"
	| "streamlabs"
	| "streamlabs_charity"
	| "tiltify"
	| "patreon"
	| "streamelements"
	| "tipeee"
	| "twitch_charity"
	| "voicemod"
	| "music"
	| "tiktok";

const EXAMPLE_IMAGE = asset().getAsset("img/musicExampleCover.jpg");

export const LabelItemPlaceholderList = [
	{
		tag: "TRIGGER",
		type: "string",
		category: "_trigger_",
		descriptionKey: "overlay.labels.placeholders.TRIGGER",
		backup: true,
		example: "Hello world!",
	} as const,
	{
		tag: "DATE",
		type: "date",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.DATE",
		backup: true,
		example: "08/08/2026",
	} as const,
	{
		tag: "TIME",
		type: "time",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.TIME",
		backup: true,
		example: "18:26:20",
	} as const,
	{
		tag: "DATE_TIME",
		type: "datetime",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.DATE_TIME",
		backup: true,
		example: "08/08/2026 18:26:20",
	} as const,
	{
		tag: "DAY",
		type: "day",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.DAY",
		backup: true,
		example: "8",
	} as const,
	{
		tag: "MONTH",
		type: "month",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.MONTH",
		backup: true,
		example: "9",
	} as const,
	{
		tag: "YEAR",
		type: "year",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.YEAR",
		backup: true,
		example: "2026",
	} as const,
	{
		tag: "HOURS",
		type: "hours",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.HOURS",
		backup: true,
		example: "18",
	} as const,
	{
		tag: "MINUTES",
		type: "minutes",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.MINUTES",
		backup: true,
		example: "26",
	} as const,
	{
		tag: "SECONDS",
		type: "seconds",
		category: "date",
		descriptionKey: "overlay.labels.placeholders.SECONDS",
		backup: true,
		example: "20",
	} as const,

	{
		tag: "VIEWER_COUNT_TWITCH",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.VIEWER_COUNT_TWITCH",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "FOLLOWER_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_COUNT",
		backup: true,
		example: "12345",
	} as const,
	{
		tag: "SUB_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_COUNT",
		backup: true,
		example: "422",
	} as const,
	{
		tag: "SUB_POINTS",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_POINTS",
		backup: true,
		example: "654",
	} as const,

	{
		tag: "STREAM_DURATION",
		type: "duration",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.STREAM_DURATION",
		backup: true,
		example: "02:34:56",
	} as const,
	{
		tag: "STREAM_TITLE",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.STREAM_TITLE",
		backup: true,
		example: "Coding Twitchat live!",
	} as const,
	{
		tag: "STREAM_CATEGORY_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.STREAM_CATEGORY_NAME",
		backup: true,
		example: "Software and Game Development",
	} as const,
	{
		tag: "STREAM_CATEGORY_COVER",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.STREAM_CATEGORY_COVER",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "SUB_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUB_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUB_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUB_TIER",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_TIER",
		backup: true,
		example: "3",
	} as const,
	{
		tag: "SUB_MONTHS_TOTAL",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_MONTHS_TOTAL",
		backup: true,
		example: "24",
	} as const,
	{
		tag: "SUB_MONTHS_STREAK",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_MONTHS_STREAK",
		backup: true,
		example: "12",
	} as const,
	{
		tag: "SUB_MONTHS_PREPAID",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUB_MONTHS_PREPAID",
		backup: true,
		example: "6",
	} as const,

	{
		tag: "SUBGIFT_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUBGIFT_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUBGIFT_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUBGIFT_TIER",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_TIER",
		backup: true,
		example: "3",
	} as const,
	{
		tag: "SUBGIFT_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_COUNT",
		backup: true,
		example: "5",
	} as const,

	{
		tag: "VIEWER_COUNT_YOUTUBE",
		type: "number",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.VIEWER_COUNT_YOUTUBE",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "SUB_YOUTUBE_NAME",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUB_YOUTUBE_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUB_YOUTUBE_ID",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUB_YOUTUBE_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUB_YOUTUBE_AVATAR",
		type: "image",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUB_YOUTUBE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUB_YOUTUBE_TIER",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUB_YOUTUBE_TIER",
		backup: true,
		example: "Member",
	} as const,

	{
		tag: "SUBGIFT_YOUTUBE_NAME",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_YOUTUBE_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUBGIFT_YOUTUBE_ID",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_YOUTUBE_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUBGIFT_YOUTUBE_AVATAR",
		type: "image",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_YOUTUBE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUBGIFT_YOUTUBE_TIER",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_YOUTUBE_TIER",
		backup: true,
		example: "Member",
	} as const,
	{
		tag: "SUBGIFT_YOUTUBE_COUNT",
		type: "number",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_YOUTUBE_COUNT",
		backup: true,
		example: "5",
	} as const,

	{
		tag: "FOLLOWER_YOUTUBE_USER",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_YOUTUBE_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "FOLLOWER_YOUTUBE_AVATAR",
		type: "number",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_YOUTUBE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "VIEWER_COUNT",
		type: "number",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.VIEWER_COUNT",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "SUB_GENERIC_NAME",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUB_GENERIC_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUB_GENERIC_ID",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUB_GENERIC_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUB_GENERIC_AVATAR",
		type: "image",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUB_GENERIC_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUB_GENERIC_TIER",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUB_GENERIC_TIER",
		backup: true,
		example: "3",
	} as const,

	{
		tag: "SUBGIFT_GENERIC_NAME",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_GENERIC_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUBGIFT_GENERIC_ID",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_GENERIC_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUBGIFT_GENERIC_AVATAR",
		type: "image",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_GENERIC_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUBGIFT_GENERIC_TIER",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_GENERIC_TIER",
		backup: true,
		example: "3",
	} as const,
	{
		tag: "SUBGIFT_GENERIC_COUNT",
		type: "number",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.SUBGIFT_GENERIC_COUNT",
		backup: true,
		example: "5",
	} as const,

	{
		tag: "SUPER_CHAT_NAME",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_CHAT_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUPER_CHAT_ID",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_CHAT_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUPER_CHAT_AVATAR",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_CHAT_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUPER_CHAT_AMOUNT",
		type: "number",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_CHAT_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,

	{
		tag: "SUPER_STICKER_NAME",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_STICKER_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "SUPER_STICKER_ID",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_STICKER_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "SUPER_STICKER_AVATAR",
		type: "string",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_STICKER_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "SUPER_STICKER_AMOUNT",
		type: "number",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_STICKER_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,
	{
		tag: "SUPER_STICKER_IMAGE",
		type: "image",
		category: "youtube",
		descriptionKey: "overlay.labels.placeholders.SUPER_STICKER_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "CHEER_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.CHEER_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "CHEER_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.CHEER_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "CHEER_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.CHEER_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "CHEER_AMOUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.CHEER_AMOUNT",
		backup: true,
		example: "500",
	} as const,

	{
		tag: "FOLLOWER_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "FOLLOWER_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "FOLLOWER_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "FOLLOWER_GENERIC_NAME",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_GENERIC_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "FOLLOWER_GENERIC_ID",
		type: "string",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_GENERIC_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "FOLLOWER_GENERIC_AVATAR",
		type: "image",
		category: "generic",
		descriptionKey: "overlay.labels.placeholders.FOLLOWER_GENERIC_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "REWARD_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.REWARD_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "REWARD_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.REWARD_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "REWARD_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.REWARD_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "REWARD_ICON",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.REWARD_ICON",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "REWARD_TITLE",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.REWARD_TITLE",
		backup: true,
		example: "Highlight my message",
	} as const,

	{
		tag: "RAID_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.RAID_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "RAID_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.RAID_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "RAID_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.RAID_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "RAID_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.RAID_COUNT",
		backup: true,
		example: "42",
	} as const,

	{
		tag: "WATCH_STREAK_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.WATCH_STREAK_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "WATCH_STREAK_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.WATCH_STREAK_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "WATCH_STREAK_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.WATCH_STREAK_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "WATCH_STREAK_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.WATCH_STREAK_COUNT",
		backup: true,
		example: "10",
	} as const,

	{
		tag: "MODIVERSARY_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.MODIVERSARY_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "MODIVERSARY_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.MODIVERSARY_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "MODIVERSARY_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.MODIVERSARY_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "MODIVERSARY_COUNT",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.MODIVERSARY_COUNT",
		backup: true,
		example: "3",
	} as const,

	{
		tag: "POWER_UP_GIANTIFIED_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_GIANTIFIED_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "POWER_UP_GIANTIFIED_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_GIANTIFIED_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "POWER_UP_GIANTIFIED_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_GIANTIFIED_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "POWER_UP_GIANTIFIED_CODE",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_GIANTIFIED_CODE",
		backup: true,
		example: "Kappa",
	} as const,
	{
		tag: "POWER_UP_GIANTIFIED_IMAGE",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_GIANTIFIED_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "POWER_UP_CELEBRATION_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CELEBRATION_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "POWER_UP_CELEBRATION_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CELEBRATION_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "POWER_UP_CELEBRATION_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CELEBRATION_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "POWER_UP_CELEBRATION_CODE",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CELEBRATION_CODE",
		backup: true,
		example: "Kappa",
	} as const,
	{
		tag: "POWER_UP_CELEBRATION_IMAGE",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CELEBRATION_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "POWER_UP_MESSAGE_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_MESSAGE_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "POWER_UP_MESSAGE_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_MESSAGE_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "POWER_UP_MESSAGE_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_MESSAGE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "POWER_UP_CUSTOM_ID",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CUSTOM_ID",
		backup: true,
		example: "29961813",
	} as const,
	{
		tag: "POWER_UP_CUSTOM_NAME",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CUSTOM_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "POWER_UP_CUSTOM_AVATAR",
		type: "image",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CUSTOM_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "POWER_UP_CUSTOM_TITLE",
		type: "string",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CUSTOM_TITLE",
		backup: true,
		example: "Hydrate!",
	} as const,
	{
		tag: "POWER_UP_CUSTOM_COST",
		type: "number",
		category: "twitch",
		descriptionKey: "overlay.labels.placeholders.POWER_UP_CUSTOM_COST",
		backup: true,
		example: "500",
	} as const,

	{
		tag: "KOFI_TIP_NAME",
		type: "string",
		category: "kofi",
		descriptionKey: "overlay.labels.placeholders.KOFI_TIP_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "KOFI_TIP_AMOUNT",
		type: "number",
		category: "kofi",
		descriptionKey: "overlay.labels.placeholders.KOFI_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,
	{
		tag: "KOFI_MERCH_USER",
		type: "string",
		category: "kofi",
		descriptionKey: "overlay.labels.placeholders.KOFI_MERCH_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "KOFI_MERCH_NAME",
		type: "string",
		category: "kofi",
		descriptionKey: "overlay.labels.placeholders.KOFI_MERCH_NAME",
		backup: true,
		example: "Twitchat mug",
	} as const,
	{
		tag: "KOFI_MERCH_AMOUNT",
		type: "number",
		category: "kofi",
		descriptionKey: "overlay.labels.placeholders.KOFI_MERCH_AMOUNT",
		backup: true,
		example: "$19.99",
	} as const,

	{
		tag: "STREAMLABS_TIP_NAME",
		type: "string",
		category: "streamlabs",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_TIP_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "STREAMLABS_TIP_AMOUNT",
		type: "number",
		category: "streamlabs",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,
	{
		tag: "STREAMLABS_MERCH_USER",
		type: "string",
		category: "streamlabs",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_MERCH_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "STREAMLABS_MERCH_NAME",
		type: "string",
		category: "streamlabs",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_MERCH_NAME",
		backup: true,
		example: "Twitchat mug",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_NAME",
		type: "string",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_NAME",
		backup: true,
		example: "Save the ferrets",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_IMAGE",
		type: "image",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "STREAMLABS_CHARITY_RAISED",
		type: "number",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_RAISED",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_RAISED_PERSONNAL",
		type: "number",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_RAISED_PERSONNAL",
		backup: true,
		example: "420",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_GOAL",
		type: "number",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_GOAL",
		backup: true,
		example: "10000",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_LAST_TIP_USER",
		type: "string",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_LAST_TIP_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "STREAMLABS_CHARITY_LAST_TIP_AMOUNT",
		type: "number",
		category: "streamlabs_charity",
		descriptionKey: "overlay.labels.placeholders.STREAMLABS_CHARITY_LAST_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,

	{
		tag: "TILTIFY_LAST_TIP_USER",
		type: "string",
		category: "tiltify",
		descriptionKey: "overlay.labels.placeholders.TILTIFY_LAST_TIP_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TILTIFY_LAST_TIP_AMOUNT",
		type: "number",
		category: "tiltify",
		descriptionKey: "overlay.labels.placeholders.TILTIFY_LAST_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,

	{
		tag: "PATREON_USER",
		type: "string",
		category: "patreon",
		descriptionKey: "overlay.labels.placeholders.PATREON_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "PATREON_AMOUNT",
		type: "number",
		category: "patreon",
		descriptionKey: "overlay.labels.placeholders.PATREON_AMOUNT",
		backup: true,
		example: "$5.00",
	} as const,
	{
		tag: "PATREON_TITLE",
		type: "string",
		category: "patreon",
		descriptionKey: "overlay.labels.placeholders.PATREON_TITLE",
		backup: true,
		example: "Premium tier",
	} as const,
	{
		tag: "PATREON_AVATAR",
		type: "image",
		category: "patreon",
		descriptionKey: "overlay.labels.placeholders.PATREON_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "PATREON_MEMBER_COUNT",
		type: "number",
		category: "patreon",
		descriptionKey: "overlay.labels.placeholders.PATREON_MEMBER_COUNT",
		backup: true,
		example: "123",
	} as const,

	{
		tag: "STREAMELEMENTS_TIP_NAME",
		type: "string",
		category: "streamelements",
		descriptionKey: "overlay.labels.placeholders.STREAMELEMENTS_TIP_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "STREAMELEMENTS_TIP_AMOUNT",
		type: "number",
		category: "streamelements",
		descriptionKey: "overlay.labels.placeholders.STREAMELEMENTS_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,

	{
		tag: "TIPEEE_TIP_NAME",
		type: "string",
		category: "tipeee",
		descriptionKey: "overlay.labels.placeholders.TIPEEE_TIP_NAME",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIPEEE_TIP_AMOUNT",
		type: "number",
		category: "tipeee",
		descriptionKey: "overlay.labels.placeholders.TIPEEE_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,

	{
		tag: "TWITCH_CHARITY_NAME",
		type: "string",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_NAME",
		backup: true,
		example: "Save the ferrets",
	} as const,
	{
		tag: "TWITCH_CHARITY_IMAGE",
		type: "image",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "TWITCH_CHARITY_RAISED",
		type: "number",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_RAISED",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "TWITCH_CHARITY_GOAL",
		type: "number",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_GOAL",
		backup: true,
		example: "10000",
	} as const,
	{
		tag: "TWITCH_CHARITY_LAST_TIP_USER",
		type: "string",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_LAST_TIP_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TWITCH_CHARITY_LAST_TIP_AMOUNT",
		type: "number",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_LAST_TIP_AMOUNT",
		backup: true,
		example: "$13.37",
	} as const,
	{
		tag: "TWITCH_CHARITY_LAST_TIP_AVATAR",
		type: "image",
		category: "twitch_charity",
		descriptionKey: "overlay.labels.placeholders.TWITCH_CHARITY_LAST_TIP_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "VOICEMOD_EFFECT_TITLE",
		type: "number",
		category: "voicemod",
		descriptionKey: "overlay.labels.placeholders.VOICEMOD_EFFECT_TITLE",
		backup: true,
		example: "Robot",
	} as const,
	{
		tag: "VOICEMOD_EFFECT_ICON",
		type: "number",
		category: "voicemod",
		descriptionKey: "overlay.labels.placeholders.VOICEMOD_EFFECT_ICON",
		backup: false,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "MUSIC_TITLE",
		type: "string",
		category: "music",
		descriptionKey: "overlay.labels.placeholders.MUSIC_TITLE",
		backup: true,
		example: "Mitchiri Neko March",
	} as const,
	{
		tag: "MUSIC_ARTIST",
		type: "string",
		category: "music",
		descriptionKey: "overlay.labels.placeholders.MUSIC_ARTIST",
		backup: true,
		example: "Mitchiri Neko",
	} as const,
	{
		tag: "MUSIC_ALBUM",
		type: "string",
		category: "music",
		descriptionKey: "overlay.labels.placeholders.MUSIC_ALBUM",
		backup: true,
		example: "Mitchiri Neko",
	} as const,
	{
		tag: "MUSIC_COVER",
		type: "image",
		category: "music",
		descriptionKey: "overlay.labels.placeholders.MUSIC_COVER",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "VIEWER_COUNT_TIKTOK",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.VIEWER_COUNT_TIKTOK",
		backup: true,
		example: "1337",
	} as const,
	{
		tag: "TIKTOK_LIKE_TOTAL",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_LIKE_TOTAL",
		backup: true,
		example: "12345",
	} as const,
	{
		tag: "TIKTOK_LIKE_USER",
		type: "string",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_LIKE_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIKTOK_LIKE_AVATAR",
		type: "image",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_LIKE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "TIKTOK_LIKE_COUNT",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_LIKE_COUNT",
		backup: true,
		example: "42",
	} as const,

	{
		tag: "TIKTOK_FOLLOWER_USER",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_FOLLOWER_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIKTOK_FOLLOWER_AVATAR",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_FOLLOWER_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "TIKTOK_SUB_USER",
		type: "string",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_SUB_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIKTOK_SUB_AVATAR",
		type: "image",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_SUB_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "TIKTOK_SHARE_USER",
		type: "string",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_SHARE_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIKTOK_SHARE_AVATAR",
		type: "image",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_SHARE_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,

	{
		tag: "TIKTOK_GIFT_USER",
		type: "string",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_GIFT_USER",
		backup: true,
		example: "Durss",
	} as const,
	{
		tag: "TIKTOK_GIFT_AVATAR",
		type: "image",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_GIFT_AVATAR",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "TIKTOK_GIFT_IMAGE",
		type: "image",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_GIFT_IMAGE",
		backup: true,
		example: EXAMPLE_IMAGE,
	} as const,
	{
		tag: "TIKTOK_GIFT_COUNT",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_GIFT_COUNT",
		backup: true,
		example: "5",
	} as const,
	{
		tag: "TIKTOK_GIFT_DIAMONDS",
		type: "number",
		category: "tiktok",
		descriptionKey: "overlay.labels.placeholders.TIKTOK_GIFT_DIAMONDS",
		backup: true,
		example: "500",
	} as const,
] as const satisfies {
	tag: string;
	type: PlaceholderType;
	category: PlaceholderCategory;
	descriptionKey: string;
	backup: boolean;
	example: string;
}[];

export type LabelItemPlaceholderTag = (typeof LabelItemPlaceholderList)[number]["tag"];
